import { lazy, Suspense } from 'react';
import { Grid, Skeleton, Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * WidgetRenderer - Dynamic widget factory component
 * 
 * Features:
 * - Lazy loading (React.lazy + Suspense)
 * - Skeleton loader fallback
 * - Dynamic component mapping
 * - MUI Grid v2 (size prop)
 * - Error handling for missing components
 * 
 * Phase 3-4: UserType-based widget rendering (Phase 4 refactoring)
 * Phase 5: Permission-based widget rendering
 */

// ============================================================================
// LAZY LOAD WIDGET COMPONENTS
// ============================================================================

const WIDGET_COMPONENTS = {
  // ===== COMMON WIDGETS =====
  WelcomeCard: lazy(() => import('./common/WelcomeCard')),
  EmailVerificationBanner: lazy(() => import('../dashboard/EmailVerificationBanner')),
  QuickActionsWidget: lazy(() => import('./common/QuickActionsWidget')),
  
  // ===== USER WIDGETS =====
  MyDocumentsWidget: lazy(() => import('./user/MyDocumentsWidget')),
  TemplatesWidget: lazy(() => import('./user/TemplatesWidget')),
  RecentDocumentsWidget: lazy(() => import('./user/RecentDocumentsWidget')),
  RecentActivityWidget: lazy(() => import('./user/RecentActivityWidget')),
  
  // ===== COMPANY WIDGETS =====
  CompanyStatsWidget: lazy(() => import('./company/CompanyStatsWidget')),
  UsersSummaryWidget: lazy(() => import('./company/UsersSummaryWidget')),
  CompanyInfoWidget: lazy(() => import('./company/CompanyInfoWidget')),
  InvitesWidget: lazy(() => import('./company/InvitesWidget')),
  
  // ===== ADMIN WIDGETS (Phase 5) =====
  SystemStatsWidget: lazy(() => import('./admin/SystemStatsWidget')),
  UsersOverviewWidget: lazy(() => import('./admin/UsersOverviewWidget')),
  
  // ===== PHASE 7: CREDIT SYSTEM WIDGETS =====
  CreditBalanceWidget: lazy(() => import('./common/CreditBalanceWidget')),
  CreditReportsWidget: lazy(() => import('./admin/CreditReportsWidget')),
  CompanyCreditWidget: lazy(() => import('./company/CompanyCreditWidget')),
  CreditHistoryWidget: lazy(() => import('./user/CreditHistoryWidget'))
};

// ============================================================================
// SKELETON LOADER
// ============================================================================

/**
 * WidgetSkeleton - Loading placeholder for widgets
 * @param {Object} gridSize - MUI Grid v2 size prop
 */
const WidgetSkeleton = ({ gridSize }) => (
  <Grid size={gridSize}>
    <Box sx={{ p: 2 }}>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      <Skeleton variant="text" sx={{ mt: 2, fontSize: '1.5rem' }} />
      <Skeleton variant="text" width="60%" sx={{ fontSize: '1rem' }} />
    </Box>
  </Grid>
);

WidgetSkeleton.propTypes = {
  gridSize: PropTypes.object
};

// ============================================================================
// WIDGET RENDERER COMPONENT
// ============================================================================

/**
 * WidgetRenderer - Renders a list of widgets dynamically
 * 
 * @param {Array} widgets - Array of widget configurations from getDashboardWidgets()
 * @returns {JSX.Element} Rendered widgets in MUI Grid
 */
const WidgetRenderer = ({ widgets }) => {
  // Guard: No widgets to render
  if (!widgets || widgets.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          color: 'text.secondary'
        }}
      >
        <Typography variant="h6">Henüz widget yok</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Dashboard widget'ları yükleniyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {widgets.map((widget) => {
        const Component = WIDGET_COMPONENTS[widget.component];

        // Guard: Component not found in registry
        if (!Component) {
          console.warn(`[WidgetRenderer] Widget component not found: ${widget.component}`);
          return null;
        }

        return (
          <Grid key={widget.id} size={widget.gridSize}>
            <Suspense fallback={<WidgetSkeleton gridSize={widget.gridSize} />}>
              <Component />
            </Suspense>
          </Grid>
        );
      })}
    </Grid>
  );
};

WidgetRenderer.propTypes = {
  widgets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      component: PropTypes.string.isRequired,
      gridSize: PropTypes.object
    })
  ).isRequired
};

export default WidgetRenderer;

